import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './Calendar.module.css';

const Calendar = ({
  mode = 'range',
  value = null,
  onChange,
  minDate = null,
  maxDate = null,
  columns = 2,
  variant = 'default',
  onClear,
  onClose,
  onConfirm,
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => value?.start || new Date());
  const [hoverDate, setHoverDate] = useState(null);
  // Figma 69:29193 Month Picker — 좌측 월 헤더의 chevron으로 열고 닫는다
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => (value?.start || new Date()).getFullYear());

  const toggleMonthPicker = () => {
    setPickerYear(currentMonth.getFullYear());
    setShowMonthPicker(open => !open);
  };

  const handleMonthPick = monthIndex => {
    setCurrentMonth(new Date(pickerYear, monthIndex, 1));
    setShowMonthPicker(false);
  };

  const getNextMonth = date => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + 1);
    return next;
  };

  const nextMonth = getNextMonth(currentMonth);

  const handlePrevMonth = () => {
    const prev = new Date(currentMonth);
    prev.setMonth(prev.getMonth() - 1);
    setCurrentMonth(prev);
  };

  const handleNextMonth = () => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() + 1);
    setCurrentMonth(next);
  };

  const handleDateClick = date => {
    if (mode === 'single') {
      onChange?.(date);
    } else {
      if (!value?.start || (value?.start && value?.end)) {
        onChange?.({ start: date, end: null });
      } else {
        if (date < value.start) {
          onChange?.({ start: date, end: value.start });
        } else {
          onChange?.({ start: value.start, end: date });
        }
      }
    }
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const isSelected = date => {
    if (mode === 'single') {
      return value && isSameDay(date, value);
    } else {
      if (!value?.start) return false;
      if (!value?.end) return isSameDay(date, value.start);
      return isSameDay(date, value.start) || isSameDay(date, value.end);
    }
  };

  const isInRange = date => {
    if (mode !== 'range' || !value?.start) return false;
    const endDate = value?.end || hoverDate;
    if (!endDate) return false;
    const start = value.start < endDate ? value.start : endDate;
    const end = value.start < endDate ? endDate : value.start;
    return date > start && date < end;
  };

  const isRangeStart = date => {
    if (mode !== 'range' || !value?.start) return false;
    const endDate = value?.end || hoverDate;
    if (!endDate) return false;
    const start = value.start < endDate ? value.start : endDate;
    const end = value.start < endDate ? endDate : value.start;
    if (isSameDay(start, end)) return false;
    return isSameDay(date, start);
  };

  const isRangeEnd = date => {
    if (mode !== 'range' || !value?.start) return false;
    const endDate = value?.end || hoverDate;
    if (!endDate) return false;
    const start = value.start < endDate ? value.start : endDate;
    const end = value.start < endDate ? endDate : value.start;
    if (isSameDay(start, end)) return false;
    return isSameDay(date, end);
  };

  const isDisabled = date => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const isToday = date => {
    const today = new Date();
    return isSameDay(date, today);
  };

  return (
    <div className={`${styles.calendar} ${variant === 'event' ? styles.eventCalendar : ''}`}>
      <div className={columns === 1 ? styles.calendarGridSingle : styles.calendarGrid}>
        {columns === 1 ? (
          <MonthView
            month={currentMonth}
            onDateClick={handleDateClick}
            isSelected={isSelected}
            isInRange={isInRange}
            isRangeStart={isRangeStart}
            isRangeEnd={isRangeEnd}
            isDisabled={isDisabled}
            isToday={isToday}
            onHoverDate={setHoverDate}
            showNavigation="both"
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            variant={variant}
          />
        ) : (
          <>
            <MonthView
              month={currentMonth}
              onDateClick={handleDateClick}
              isSelected={isSelected}
              isInRange={isInRange}
              isRangeStart={isRangeStart}
              isRangeEnd={isRangeEnd}
              isDisabled={isDisabled}
              isToday={isToday}
              onHoverDate={setHoverDate}
              showNavigation="prev"
              onPrevMonth={handlePrevMonth}
              onToggleMonthPicker={toggleMonthPicker}
              variant={variant}
            />
            <MonthView
              month={nextMonth}
              onDateClick={handleDateClick}
              isSelected={isSelected}
              isInRange={isInRange}
              isRangeStart={isRangeStart}
              isRangeEnd={isRangeEnd}
              isDisabled={isDisabled}
              isToday={isToday}
              onHoverDate={setHoverDate}
              showNavigation="next"
              onNextMonth={handleNextMonth}
              variant={variant}
            />
          </>
        )}
        {showMonthPicker && (
          <MonthPicker
            year={pickerYear}
            onPrevYear={() => setPickerYear(y => y - 1)}
            onNextYear={() => setPickerYear(y => y + 1)}
            onPick={handleMonthPick}
          />
        )}
      </div>
      {variant === 'event' && (
        <div className={styles.eventFooter}>
          <button type="button" onClick={onClear}>선택 취소</button>
          <div>
            <button type="button" onClick={onClose}>닫기</button>
            <button type="button" className={styles.confirmButton} onClick={onConfirm}>확인</button>
          </div>
        </div>
      )}
    </div>
  );
};

const MonthView = ({
  month,
  onDateClick,
  isSelected,
  isInRange,
  isRangeStart,
  isRangeEnd,
  isDisabled,
  isToday,
  onHoverDate,
  showNavigation,
  onPrevMonth,
  onNextMonth,
  onToggleMonthPicker,
  variant,
}) => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();

  const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  const getDaysInMonth = () => {
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, monthIndex, i));
    }
    return days;
  };

  const days = getDaysInMonth();

  return (
    <div className={styles.monthView}>
      <div className={styles.monthHeader}>
        {variant !== 'event' && (showNavigation === 'prev' || showNavigation === 'both') && (
          <button className={styles.navButton} onClick={onPrevMonth}>
            <ChevronLeft />
          </button>
        )}
        {variant === 'event' && showNavigation === 'prev' ? (
          <button type="button" className={styles.monthTitle} onClick={onToggleMonthPicker}>
            {year}년 {monthNames[monthIndex]}
            <ChevronDown size={20} />
          </button>
        ) : (
          <div className={styles.monthTitle}>
            {year}년 {monthNames[monthIndex]}
          </div>
        )}
        {variant === 'event' && showNavigation === 'next' ? (
          <div className={styles.eventNavigation}>
            <button type="button" onClick={onPrevMonth}><ChevronLeft size={20} /></button>
            <button type="button" onClick={onNextMonth}><ChevronRight size={20} /></button>
          </div>
        ) : (showNavigation === 'next' || showNavigation === 'both') && (
          <button className={styles.navButton} onClick={onNextMonth}><ChevronRight /></button>
        )}
      </div>

      <div className={styles.weekdays}>
        {dayNames.map((day, index) => (
          <div
            key={index}
            className={`${styles.weekday} ${index === 0 ? styles.sunday : index === 6 ? styles.saturday : ''}`}
          >
            {day}
          </div>
        ))}
      </div>

      <div className={styles.daysGrid}>
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className={styles.emptyDay}></div>;
          }

          const selected = isSelected(date);
          const inRange = isInRange(date);
          const rangeStart = isRangeStart(date);
          const rangeEnd = isRangeEnd(date);
          const disabled = isDisabled(date);
          const today = isToday(date);
          const isSunday = date.getDay() === 0;
          const isSaturday = date.getDay() === 6;

          return (
            <button
              key={index}
              className={`${styles.day} ${selected ? styles.selected : ''} ${rangeStart ? styles.rangeStart : ''} ${rangeEnd ? styles.rangeEnd : ''} ${inRange ? styles.inRange : ''} ${disabled ? styles.disabled : ''} ${today ? styles.today : ''} ${isSunday ? styles.sunday : isSaturday ? styles.saturday : ''}`}
              onClick={() => !disabled && onDateClick(date)}
              onMouseEnter={() => onHoverDate?.(date)}
              disabled={disabled}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* Figma 69:29193 Month Picker — 354x256, 헤더 좌측정렬, 12개월 3열 그리드 */
const MonthPicker = ({ year, onPrevYear, onNextYear, onPick }) => (
  <div className={styles.monthPicker}>
    <div className={styles.monthPickerHead}>
      <button type="button" onClick={onPrevYear} aria-label="이전 해">
        <ChevronLeft size={20} />
      </button>
      <span>{year}년</span>
      <button type="button" onClick={onNextYear} aria-label="다음 해">
        <ChevronRight size={20} />
      </button>
    </div>
    <div className={styles.monthPickerGrid}>
      {Array.from({ length: 12 }, (_, i) => (
        <button type="button" key={i} onClick={() => onPick(i)}>
          {i + 1}월
        </button>
      ))}
    </div>
  </div>
);

export default Calendar;
